import { z } from 'zod';

/**
 * فلترة الجداول بالأعمدة.
 *
 * زرّ «فلتر» فوق كل قائمة رئيسية يضع على كل عمود شرطًا: المسؤول فلان،
 * والتاريخ بين يومين، والحالة واحدة من ثلاث. والشروط تُرسل إلى الخادم في
 * معامل واحد `f` (JSON) لا في معامل لكل عمود: القوائم مرقّمة صفحاتها في
 * الخادم، فالفلترة في المتصفح تُصفّي الصفحة المعروضة وحدها وتكذب في العدد.
 *
 * ## الأعمدة المقبولة يحدّدها كل مسار
 *
 * المعامل يحمل مفاتيح أعمدة لا أسماء حقول، والخادم يترجمها بقائمة يعرّفها
 * كل مسار — بنفس مبدأ الترتيب الآمن. مفتاح ليس في القائمة يُتجاهل، فلا
 * يُفلتر أحد على حقل لم نقصد كشفه.
 */

export const COLUMN_FILTER_KINDS = ['text', 'enum', 'user', 'date', 'number'] as const;
export type ColumnFilterKind = (typeof COLUMN_FILTER_KINDS)[number];

export const TEXT_FILTER_OPS = ['has', 'is', 'empty', 'filled', 'in', 'nin'] as const;
export type TextFilterOp = (typeof TEXT_FILTER_OPS)[number];

/**
 * `in` و`nin` قائمة القيم كما في إكسل: علامات على القيم الموجودة في العمود.
 *
 * والقائمة قد تُقصّ (عمود بآلاف الأسماء يُعرض أكثرها تكرارًا)، فمن أزال
 * علامتين من قائمة مقصوصة يريد «الكل إلا هذين» — وهو `nin` — لا «هذه
 * المئتين وحدها». و`blank` هل «(فارغ)» معلَّم.
 */
export const TEXT_FILTER_OP_LABELS: Readonly<Record<TextFilterOp, string>> = {
  has: 'يحتوي',
  is: 'يساوي',
  empty: 'فارغ',
  filled: 'غير فارغ',
  in: 'من القيم',
  nin: 'إلا القيم',
};

/** حدّ القيم المعروضة في قائمة العمود — ما زاد يُبلغ إليه بالبحث. */
export const DISTINCT_LIMIT = 200;

/** قيمة في قائمة العمود وعدد صفوفها. `null` هو «(فارغ)». */
export interface DistinctValue {
  value: string | null;
  count: number;
}

export interface DistinctResult {
  values: DistinctValue[];
  /** في العمود قيم أكثر مما عُرض. */
  truncated: boolean;
}

/** في فلتر المسؤول: «بلا مسؤول» قيمةٌ تُختار مع الأسماء. */
export const NO_USER = 'none';

const Day = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);

export const ColumnFilterValueSchema = z.discriminatedUnion('t', [
  z.object({
    t: z.literal('text'),
    op: z.enum(TEXT_FILTER_OPS),
    v: z.string().trim().max(120).optional(),
    vals: z.array(z.string().max(300)).max(DISTINCT_LIMIT).optional(),
    blank: z.boolean().optional(),
  }),
  z.object({ t: z.literal('enum'), in: z.array(z.string().max(80)).min(1).max(50) }),
  z.object({ t: z.literal('user'), in: z.array(z.string().max(40)).min(1).max(50) }),
  z.object({ t: z.literal('date'), from: Day.optional(), to: Day.optional() }),
  z.object({ t: z.literal('number'), min: z.number().optional(), max: z.number().optional() }),
]);
export type ColumnFilterValue = z.infer<typeof ColumnFilterValueSchema>;

export type ColumnFilters = Record<string, ColumnFilterValue>;

const ColumnFiltersSchema = z.record(z.string().max(60), ColumnFilterValueSchema);

/**
 * معامل `f` في الرابط: نصّ JSON يُحلَّل ويُتحقَّق منه.
 *
 * والمعطوب يُرفض لا يُتجاهل: رابطٌ محفوظ بفلتر قديم يعرض «كل شيء» بصمت
 * أسوأ من رسالة تقول إن الفلتر لم يُفهم.
 */
export const ColumnFiltersParamSchema = z
  .string()
  .max(4000)
  .optional()
  .transform((raw, ctx): ColumnFilters | undefined => {
    if (!raw) return undefined;
    let parsed: unknown;
    try {
      parsed = JSON.parse(raw);
    } catch {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'الفلتر غير مفهوم.' });
      return z.NEVER;
    }
    const checked = ColumnFiltersSchema.safeParse(parsed);
    if (!checked.success) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'الفلتر غير مفهوم.' });
      return z.NEVER;
    }
    return checked.data;
  });

/** معاملات مسار القيم المختلفة: العمود، وبحثٌ داخل قيمه. */
export const DistinctQuerySchema = z.object({
  col: z.string().trim().min(1).max(60),
  dq: z.string().trim().max(120).optional(),
});
export type DistinctQuery = z.infer<typeof DistinctQuerySchema>;

/** هل في الشرط ما يُفلتر به فعلًا — نصٌّ فارغ أو مدى بلا طرفين لا يُعدّ. */
export function isActiveFilter(f: ColumnFilterValue | undefined): f is ColumnFilterValue {
  if (!f) return false;
  switch (f.t) {
    case 'text':
      if (f.op === 'in') return true; // لا علامة على شيء = لا صفّ، وهو شرط أيضًا
      if (f.op === 'nin') return !!f.vals?.length || f.blank === false;
      return f.op === 'empty' || f.op === 'filled' || !!f.v?.trim();
    case 'enum':
    case 'user':
      return f.in.length > 0;
    case 'date':
      return !!(f.from || f.to);
    case 'number':
      return f.min !== undefined || f.max !== undefined;
  }
}

/** الشروط الفعّالة وحدها، أو undefined إن لم يبق منها شيء — لتنظيف الرابط. */
export function compactFilters(filters: ColumnFilters): ColumnFilters | undefined {
  const out: ColumnFilters = {};
  for (const [k, v] of Object.entries(filters)) if (isActiveFilter(v)) out[k] = v;
  return Object.keys(out).length ? out : undefined;
}
